import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../Auth/VerifyEmail.css";
import img from "../../../assets/images/spitractors/sms-star.png";
import { spiTractorsApi, saveSession } from "../api/spiTractorsApi";

export default function OtpLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const phone     = location.state?.phone     || "";
  const full_name = location.state?.full_name || "";
  const email     = location.state?.email     || "";

  const [code,       setCode]       = useState(["", "", "", "", "", ""]);
  const [sending,    setSending]    = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);

  const inputsRef = useRef(new Array(6).fill(null));

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (!phone) return;
    handleSendOtp(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  /* Auto-fill the 6 boxes with the test code */
  const fillTestCode = (testCode = "123456") => {
    setCode(testCode.split(""));
    inputsRef.current[5]?.focus();
  };

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(0, 1);
    const next  = [...code];
    next[index] = digit;
    setCode(next);
    if (digit && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (code[index]) {
        const next = [...code];
        next[index] = "";
        setCode(next);
        return;
      }
      if (index > 0) inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft"  && index > 0) inputsRef.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = ["", "", "", "", "", ""];
    pasted.split("").forEach((d, i) => { if (i < 6) next[i] = d; });
    setCode(next);
    inputsRef.current[Math.min(pasted.length, 6) - 1]?.focus();
  };

  const handleSendOtp = async (silent = false) => {
    if (!phone) {
      alert("Missing phone number. Go back and enter your phone.");
      return;
    }
    try {
      setSending(true);
      const res = await spiTractorsApi.otpSend({ phone, full_name, email });

      if (res?.test_mode === true) {
        setIsTestMode(true);
        fillTestCode(res?.test_otp || "123456");
      } else {
        setIsTestMode(false);
        if (!silent) alert("OTP sent to " + phone);
      }
    } catch (e) {
      if (!silent) alert(e?.message || "Unable to send OTP");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    const otp = code.join("");
    if (!phone)           { alert("Missing phone number. Go back."); return; }
    if (otp.length !== 6) { alert("Please enter the 6-digit code."); return; }

    try {
      setLoading(true);
      const res   = await spiTractorsApi.otpVerify({ phone, code: otp });
      const user  = res?.data?.user;
      const token = res?.data?.token;

      if (!user || !token) throw new Error("Login succeeded but session data missing.");

      saveSession(user, token);

      const draft = location.state?.requestDraft || null;
      if (draft) localStorage.setItem("spiRequestDraft", JSON.stringify(draft));

      navigate("/Spi_Tractors_Request/", {
        replace: true,
        state:   { requestDraft: draft },
      });
    } catch (e) {
      alert(e?.message || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="verify-page">
      <div className="verify-back" onClick={() => navigate(-1)} aria-label="Back">←</div>

      <div className="verify-center">
        <div className="verify-icon" aria-hidden="true">
          <img src={img} alt="otp" />
        </div>

        {/* Test mode banner */}
        {isTestMode && (
          <div style={{
            background: "#fefce8",
            border: "1px solid #fde047",
            borderRadius: 10,
            padding: "10px 16px",
            marginBottom: 14,
            fontSize: 13,
            color: "#713f12",
            textAlign: "center",
            lineHeight: 1.6,
          }}>
            <b>🧪 Test Mode Active</b><br />
            OTP auto-filled as <b style={{ fontSize: 16, letterSpacing: 2 }}>123456</b><br />
            <span style={{ opacity: 0.75 }}>Just press Verify & Continue</span>
          </div>
        )}

        <p className="verify-text">
          {isTestMode
            ? "Test mode — use the code below."
            : <>Code sent to <span className="verify-email">{phone || "your phone"}</span></>
          }
        </p>

        <div className="otp-row">
          {code.map((v, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              className="otp-box"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={v}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>

        {/* Re-fill button in case boxes get cleared */}
        {isTestMode && (
          <button
            type="button"
            onClick={() => fillTestCode("123456")}
            style={{
              background: "none",
              border: "1px solid #fde047",
              borderRadius: 8,
              padding: "5px 14px",
              fontSize: 12,
              color: "#713f12",
              cursor: "pointer",
              marginTop: 6,
            }}
          >
            Re-fill test code (123456)
          </button>
        )}

        <div
          className="resend"
          onClick={() => !sending && handleSendOtp(false)}
          style={{ cursor: sending ? "not-allowed" : "pointer", opacity: sending ? 0.6 : 1 }}
        >
          {sending ? "Sending…" : "Resend code"}
        </div>

        <button className="verify-btn" onClick={handleVerify} disabled={loading}>
          {loading ? "Verifying…" : "Verify & Continue"}
        </button>
      </div>
    </div>
  );
}
